import * as tus from 'tus-js-client';

import { supabaseClient } from '../supabase';

/**
 * Envio de vídeo do painel para o Bunny (história 3).
 *
 * **O arquivo não passa pelo nosso servidor.** O painel pede uma credencial à Edge Function
 * `bunny-upload` e manda os bytes direto para o Bunny. A Edge Function guarda a chave da
 * biblioteca e devolve só uma **assinatura TUS**: vale para aquele vídeo, por uma hora, e não
 * dá para criar, listar nem apagar nada com ela.
 *
 * Por que TUS e não um `PUT` simples: subir por `PUT` na API do Bunny exige a `AccessKey` da
 * biblioteca no navegador, que é exatamente o que não pode acontecer (regra 3). O protocolo
 * TUS do Bunny aceita assinatura por vídeo — é o caminho que `docs/03` já tinha previsto.
 *
 * Quem marca o episódio como pronto **não é esta tela**: o Bunny processa e chama
 * `bunny-webhook`, que vira o `status` para `ready`. Aqui, terminar o envio significa
 * "entregamos o arquivo", não "o episódio está no ar" — e a interface diz isso.
 */
export type Credencial = {
  video_id: string;
  library_id: string;
  tus_endpoint: string;
  expiration_time: number;
  signature: string;
};

/** Mensagens por código de erro da função. Em português, dizendo o que fazer. */
const MENSAGENS: Record<string, string> = {
  envio_desligado: 'Envio desligado: faltam as chaves do Bunny nos secrets.',
  so_admin: 'Sua conta não é admin.',
  sem_token: 'Sessão expirada. Entre de novo.',
  episodio_nao_encontrado: 'Episódio não existe mais. Recarregue a página.',
  bunny_recusou: 'O Bunny recusou criar o vídeo. Veja o painel da biblioteca.',
  bunny_sem_guid: 'O Bunny respondeu sem identificar o vídeo. Tente de novo.',
  falha_ao_vincular: 'O vídeo foi criado, mas não ficou ligado ao episódio. Tente de novo.',
};

export function mensagemDeErro(codigo: string | null): string {
  if (!codigo) return 'Não foi possível preparar o envio.';

  return MENSAGENS[codigo] ?? `Não foi possível preparar o envio (${codigo}).`;
}

/**
 * Confere a credencial antes de usar.
 *
 * Não é paranoia de tipo: `tus_endpoint` decide **para onde o arquivo vai**. Conferir que é o
 * endereço do Bunny custa uma linha e impede que uma resposta trocada no caminho — ou um erro
 * nosso no futuro — mande o vídeo para outro lugar com a assinatura no cabeçalho.
 */
const ENDERECO_DO_BUNNY = 'https://video.bunnycdn.com/';

export function conferirCredencial(bruta: unknown): Credencial {
  const c = bruta as Partial<Credencial> | null;

  if (!c?.video_id || !c.library_id || !c.signature || !c.expiration_time || !c.tus_endpoint) {
    throw new Error('Resposta incompleta ao preparar o envio.');
  }

  if (!c.tus_endpoint.startsWith(ENDERECO_DO_BUNNY)) {
    throw new Error('Endereço de envio inesperado. Envio cancelado.');
  }

  return c as Credencial;
}

/** Chamada real à Edge Function. Separada para o teste poder trocar. */
type Invocar = (corpo: { episode_id: string; titulo?: string | undefined }) => Promise<{
  data: unknown;
  error: unknown;
}>;

const invocarPadrao: Invocar = (corpo) =>
  supabaseClient.functions.invoke('bunny-upload', { body: corpo });

/** Tenta descobrir o código de erro que a função devolveu no corpo. */
async function codigoDoErro(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: unknown })?.context;

  if (ctx instanceof Response) {
    try {
      const corpo = (await ctx.clone().json()) as { erro?: string };
      return corpo.erro ?? null;
    } catch {
      return null;
    }
  }

  return null;
}

export async function pedirCredencial(
  episodeId: string,
  titulo?: string,
  invocar: Invocar = invocarPadrao,
): Promise<Credencial> {
  const { data, error } = await invocar({ episode_id: episodeId, titulo });

  if (error) throw new Error(mensagemDeErro(await codigoDoErro(error)));

  return conferirCredencial(data);
}

/**
 * Manda os bytes por TUS.
 *
 * Sem `chunkSize`: um episódio de 40 s tem poucos megabytes, e partir em pedaços só valeria a
 * pena para arquivo grande. O que importa aqui é a **retentativa** — a rede do painel pode
 * oscilar no meio do envio, e `retryDelays` faz o `tus-js-client` voltar do ponto que o
 * servidor confirmou, em vez de começar de novo.
 */
export function enviarArquivo(args: {
  arquivo: File;
  credencial: Credencial;
  titulo: string;
  onProgresso: (fracao: number) => void;
  criarUpload?: typeof tus.Upload;
}): Promise<void> {
  const { arquivo, credencial, titulo, onProgresso } = args;
  const Upload = args.criarUpload ?? tus.Upload;

  return new Promise<void>((resolve, reject) => {
    const envio = new Upload(arquivo, {
      endpoint: credencial.tus_endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        AuthorizationSignature: credencial.signature,
        AuthorizationExpire: String(credencial.expiration_time),
        VideoId: credencial.video_id,
        LibraryId: credencial.library_id,
      },
      metadata: { filetype: arquivo.type, title: titulo },
      onProgress: (enviado, total) => onProgresso(total > 0 ? enviado / total : 0),
      onSuccess: () => resolve(),
      onError: (erro) => reject(erro instanceof Error ? erro : new Error(String(erro))),
    });

    envio.start();
  });
}
